import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Draggable Employee Component (for unassigned list)
const DraggableEmployee = ({ employee, isActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: employee.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getEmployeeDetails = () => {
    const details = [];
    if (employee.years_of_service > 0) {
      details.push(`${employee.years_of_service} years`);
    }
    if (employee.beach) {
      details.push(employee.beach);
    }
    if (employee.emt_training) {
      details.push('EMT');
    }
    if (employee.advanced_training) {
      details.push('Advanced');
    }
    return details.join(' | ');
  };

  const getDisplayName = () => {
    // Handle cases where names might be missing or incomplete
    const firstName = employee.first_name || '';
    const lastName = employee.last_name || '';
    const username = employee.username || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (username) {
      return username;
    } else {
      return 'Unknown User';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`employee-card ${isActive ? 'dragging' : ''}`}
    >
      <div className="employee-info">
        <div className="employee-name">
          {getDisplayName()}, {employee.rank}
        </div>
        <div className="employee-details-text">
          {getEmployeeDetails()}
        </div>
      </div>
    </div>
  );
};

// Sortable Assigned Employee Component (for beach zones)
const SortableAssignedEmployee = ({ employee, vehicleAssignments }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `assigned-${employee.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getRankColor = (rank) => {
    const colors = {
      'Captain': '#dc2626',
      'Lieutenant': '#ea580c',
      'Sergeant': '#ca8a04',
      'Lifeguard': '#16a34a',
      'Rookie': '#2563eb'
    };
    return colors[rank] || '#6b7280';
  };

  const getEmployeeDetails = () => {
    const details = [];
    if (employee.years_of_service > 0) {
      details.push(`${employee.years_of_service} years`);
    }
    if (employee.beach) {
      details.push(employee.beach);
    }
    if (employee.emt_training) {
      details.push('EMT');
    }
    if (employee.advanced_training) {
      details.push('Advanced');
    }
    return details.join(' | ');
  };

  const getDisplayName = () => {
    // Handle cases where names might be missing or incomplete
    const firstName = employee.first_name || '';
    const lastName = employee.last_name || '';
    const username = employee.username || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (username) {
      return username;
    } else {
      return 'Unknown User';
    }
  };

  // Get assigned vehicle for this employee
  const assignedVehicle = vehicleAssignments[employee.id];

  return (
    <div
      ref={setNodeRef}
      style={{ 
        ...style,
        borderLeft: `4px solid ${getRankColor(employee.rank)}`,
        cursor: 'grab'
      }}
      {...attributes}
      {...listeners}
      className="assigned-employee"
    >
      <div className="employee-name">
        {getDisplayName()}, {employee.rank}
      </div>
      <div className="employee-details-text">
        {getEmployeeDetails()}
      </div>
      {assignedVehicle && (
        <div className="employee-vehicle">
          🚗 {assignedVehicle.vehicle.name}
        </div>
      )}
    </div>
  );
};

// Beach Drop Zone Component
const BeachDropZone = ({ beach, employees, onDrop, onReorder, beaches, assignments, vehicleAssignments }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `beach-${beach.id}`,
  });

  const getBeachCapacity = () => {
    // Use the max_people field from the database if available
    if (beach.max_people !== null && beach.max_people !== undefined) {
      return beach.max_people;
    }
    
    // Fallback to hardcoded values for backward compatibility
    if (beach.name.toLowerCase().includes('bay')) {
      return 1; // Bay has 1 spot each
    }
    if (beach.name.toLowerCase().includes('roam')) {
      return 7; // Roaming has up to 7 guards
    }
    return 3; // Regular beaches have max 3 (25th is now treated as normal)
  };

  const shouldShowWetSeatIndicator = () => {
    if (!beach.name.toLowerCase().includes('roam')) return false;
    
    // Check if both bay beaches have assigned rookies
    const bayBeaches = beaches.filter(b => b.name.toLowerCase().includes('bay'));
    if (bayBeaches.length < 2) return false;
    
    let bayRookieCount = 0;
    bayBeaches.forEach(bayBeach => {
      const bayEmployees = assignments[bayBeach.id] || [];
      if (bayEmployees.length > 0 && (bayEmployees[0].rank === 'Rookie' || bayEmployees[0].is_rookie)) {
        bayRookieCount++;
      }
    });
    
    return bayRookieCount === 2; // Both bay beaches have rookies
  };

  const capacity = getBeachCapacity();
  const isFull = employees.length >= capacity;

  return (
    <div 
      ref={setNodeRef}
      className={`beach-zone ${isFull ? 'full' : ''} ${isOver ? 'drag-over' : ''}`}
    >
      <div className="beach-header">
        <h3>{beach.name}</h3>
        <span className="capacity">
          {employees.length}/{capacity}
          {shouldShowWetSeatIndicator() && (
            <span className="wet-seat-indicator">+ Wet Seat</span>
          )}
        </span>
      </div>
      <div className="beach-employees">
        <SortableContext
          items={employees.map(emp => `assigned-${emp.id}`)}
          strategy={verticalListSortingStrategy}
        >
                    {employees.map((employee) => (
            <SortableAssignedEmployee 
              key={employee.id} 
              employee={employee} 
              vehicleAssignments={vehicleAssignments}
            />
          ))}
        </SortableContext>
        {employees.length < capacity && (
          <div className="empty-slot">
            Drop lifeguard here
          </div>
        )}
      </div>
    </div>
  );
};

// Unassigned Section Component (now a drop target)
const UnassignedSection = ({ employees, activeId }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'unassigned-pool',
  });

  return (
    <div 
      ref={setNodeRef}
      className={`unassigned-section ${isOver ? 'drop-target-active' : ''}`}
    >
      <h2>Available Employees</h2>
      <div className="employees-list">
        <SortableContext
          items={employees.map(emp => emp.id)}
          strategy={verticalListSortingStrategy}
        >
          {employees.map(employee => (
            <DraggableEmployee
              key={employee.id}
              employee={employee}
              isActive={activeId === employee.id}
            />
          ))}
        </SortableContext>
        {employees.length === 0 && (
          <div className="empty-pool">
            No unassigned employees
          </div>
        )}
      </div>
    </div>
  );
};

// Non-Available Section Component
const NonAvailableSection = ({ employees, onAddToSchedule }) => {
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const handleAddEmployee = () => {
    if (selectedEmployee) {
      const employee = employees.find(emp => emp.id === parseInt(selectedEmployee));
      if (employee) {
        onAddToSchedule(employee);
        setSelectedEmployee('');
        setShowAddEmployee(false);
      }
    }
  };

  return (
    <div className="non-available-section">
      <div className="section-header">
        <h2>Non-Available Employees</h2>
      </div>
      
      {showAddEmployee && (
        <div className="add-employee-form">
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="form-control"
          >
            <option value="">Select an employee...</option>
            {employees.map(employee => (
              <option key={employee.id} value={employee.id}>
                {employee.first_name && employee.last_name 
                  ? `${employee.first_name} ${employee.last_name}` 
                  : employee.username}, {employee.rank}
              </option>
            ))}
          </select>
          <button 
            className="btn btn-primary"
            onClick={handleAddEmployee}
            disabled={!selectedEmployee}
          >
            Add to Schedule
          </button>
        </div>
      )}

      <button 
        className="btn btn-small btn-secondary"
        onClick={() => setShowAddEmployee(!showAddEmployee)}
      >
        {showAddEmployee ? 'Cancel' : 'Add Employee'}
      </button>
      
      <div className="info-text">
        Use this section to manually add employees who aren't marked as available for this date.
      </div>
    </div>
  );
};

const ScheduleGeneration = () => {
  const { user } = useAuth();
  

  
  const [beaches, setBeaches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [vehicleAssignments, setVehicleAssignments] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [showVehicleAssignment, setShowVehicleAssignment] = useState(false);
  const [notifying, setNotifying] = useState(false);

  // Add state for non-available employees
  const [nonAvailableEmployees, setNonAvailableEmployees] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch beaches
  const fetchBeaches = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/beaches');
      const activeBeaches = response.data.filter(beach => beach.is_active);
      setBeaches(activeBeaches);
    } catch (error) {
      console.error('Failed to fetch beaches:', error);
    }
  }, []);

  // Fetch vehicles
  const fetchVehicles = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/vehicles');
      const activeVehicles = response.data.filter(vehicle => vehicle.is_active);
      setVehicles(activeVehicles);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  }, []);

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/user/permissions');
      setUserPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
    }
  }, []);

  // Fetch employees and availability
  const fetchEmployeesAndAvailability = useCallback(async () => {
    try {

      
      const response = await apiClient.get(`/api/schedule-generation?date=${selectedDate}`);
      const data = response.data;

      

      setEmployees(data.allUsers);
      setAvailableEmployees(data.availableUsers);
    } catch (error) {
      console.error('Failed to fetch employees and availability:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
    }
  }, [selectedDate]);

  // Load existing schedule assignments for the selected date
  const loadExistingAssignments = useCallback(async () => {
    try {
      console.log(`📋 Loading existing assignments for ${selectedDate}...`);
      const response = await apiClient.get(`/api/schedule-assignments/${selectedDate}`);
      
      if (response.data.assignments && Object.keys(response.data.assignments).length > 0) {
        console.log(`✅ Loaded ${response.data.totalAssignments} existing assignments and ${response.data.totalVehicleAssignments || 0} vehicle assignments`);
        setAssignments(response.data.assignments);
        setVehicleAssignments(response.data.vehicleAssignments || {});
        setSaveStatus(`Loaded existing schedule (${response.data.totalAssignments} assignments, ${response.data.totalVehicleAssignments || 0} vehicles)`);
      } else {
        console.log('📄 No existing assignments found for this date');
        setAssignments({});
        setVehicleAssignments({});
        setSaveStatus('');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('📄 No existing assignments found for this date');
        setAssignments({});
        setVehicleAssignments({});
        setSaveStatus('');
      } else {
        console.error('Failed to load existing assignments:', error);
        setSaveStatus('Error loading existing assignments');
      }
    }
  }, [selectedDate]);

  // Auto-sort function based on rules
  const autoSort = useCallback(async () => {
    if (!availableEmployees.length || !beaches.length) {
      console.warn('Auto sort cancelled: No available employees or beaches');
      return;
    }

    // Clear all existing assignments before starting auto sort
    setAssignments({});
    setVehicleAssignments({});
    
    // Fetch bay assignment history for rookies (all previous assignments) to prioritize fair rotation
    let rookieBayCount = new Map(); // Map of rookie ID -> number of bay assignments
    
    try {
      // Parse selectedDate safely to avoid timezone issues
      const [year, month, day] = selectedDate.split('-').map(Number);
      const selectedDateObj = new Date(year, month - 1, day);
      
      // Get bay beach IDs
      const bayBeaches = beaches.filter(b => b.name.toLowerCase().includes('bay'));
      const bayBeachIds = bayBeaches.map(b => b.id);
      
      if (bayBeachIds.length > 0) {
        // Check assignments from all previous dates (not just last 14 days)
        const datePromises = [];
        
        // Get all previous dates by checking backwards from selected date
        // We'll check up to 365 days back to include all previous schedule generators
        for (let i = 1; i <= 365; i++) {
          const checkDate = new Date(year, month - 1, day - i);
          const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
          datePromises.push(
            apiClient.get(`/api/schedule-assignments/${dateStr}`)
              .then(response => ({ date: dateStr, data: response.data }))
              .catch(() => ({ date: dateStr, data: null }))
          );
        }
        
        const responses = await Promise.all(datePromises);
        
        responses.forEach(({ date, data }) => {
          if (!data || !data.assignments) return;
          
          bayBeachIds.forEach(beachId => {
            const beachEmployees = data.assignments[beachId] || [];
            beachEmployees.forEach(emp => {
              if (emp.rank === 'Rookie' || emp.is_rookie) {
                const currentCount = rookieBayCount.get(emp.id) || 0;
                rookieBayCount.set(emp.id, currentCount + 1);
              }
            });
          });
        });
      }
      
      // Log bay assignment counts for debugging
      if (rookieBayCount.size > 0) {
        console.log(`📊 Rookie bay assignment history (all previous assignments):`);
        rookieBayCount.forEach((count, rookieId) => {
          const rookie = availableEmployees.find(emp => emp.id === rookieId);
          if (rookie) {
            console.log(`  ${rookie.first_name} ${rookie.last_name}: ${count} bay assignments`);
          }
        });
      } else {
        console.log(`📊 No bay assignment history found for rookies`);
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch bay assignment history, proceeding with equal priority:', error.message);
    }
    
    const newAssignments = {};
    const unassigned = [...availableEmployees];
    
    // Initialize beach assignments
    beaches.forEach(beach => {
      newAssignments[beach.id] = [];
    });

    // Helper functions
    const getRankPriority = (rank) => {
      const priorities = { 'Captain': 5, 'Lieutenant': 4, 'Sergeant': 3, 'Lifeguard': 2, 'Rookie': 1 };
      return priorities[rank] || 0;
    };

    const isRookie = (employee) => employee.rank === 'Rookie' || employee.is_rookie;
    const isOfficer = (employee) => ['Captain', 'Lieutenant'].includes(employee.rank);
    const isLifeguard = (employee) => employee.rank === 'Lifeguard';
    
    // Enhanced rookie placement validation
    const canPlaceRookieOnBeach = (rookie, beach, currentAssignment) => {
      // Bay beaches: only rookies allowed
      if (beach.name.toLowerCase().includes('bay')) {
        return true;
      }
      
              // No special handling for 25th street - it's now treated as a normal beach
      
      // Regular beaches: rookies can only be placed if:
      // 1. There's already a lifeguard or better (rank >= 2), OR
      // 2. This is the first assignment and we're pairing with a non-rookie simultaneously
      const hasLifeguardOrBetter = currentAssignment.some(emp => getRankPriority(emp.rank) >= 2);
      
      // If beach is empty, rookie can only be placed if we're about to add a non-rookie too
      if (currentAssignment.length === 0) {
        return false; // Don't place rookie first - wait for non-rookie
      }
      
      return hasLifeguardOrBetter;
    };
    
    // Check if a beach assignment is valid (no rookies sitting alone)
    const validateBeachAssignment = (beach, employees) => {
      const rookies = employees.filter(isRookie);
      const nonRookies = employees.filter(emp => !isRookie(emp));
      
      // Bay beaches: only rookies allowed
      if (beach.name.toLowerCase().includes('bay')) {
        return nonRookies.length === 0;
      }
      
      // Other beaches: rookies must be paired with at least one non-rookie
      if (rookies.length > 0 && nonRookies.length === 0) {
        return false; // Rookies sitting alone
      }
      
      return true;
    };

    // Sort employees by priority (officers first, then by seniority)
    // Apply Riggs family bonus: add 1 year of experience for seniority calculations
    unassigned.sort((a, b) => {
      const rankDiff = getRankPriority(b.rank) - getRankPriority(a.rank);
      if (rankDiff !== 0) return rankDiff;
      
      // Add 1 year bonus for anyone with last name "Riggs" (case insensitive)
      const aExperience = a.years_of_service + (a.last_name?.toLowerCase() === 'riggs' ? 1 : 0);
      const bExperience = b.years_of_service + (b.last_name?.toLowerCase() === 'riggs' ? 1 : 0);
      
      return bExperience - aExperience;
    });

    // Calculate minimum staffing requirements
    const calculateMinimumStaffing = () => {
      let minRequired = 0;
      const bayBeaches = beaches.filter(b => b.name.toLowerCase().includes('bay'));
      const regularBeaches = beaches.filter(b => 
        !b.name.toLowerCase().includes('bay') && 
        !b.name.toLowerCase().includes('roam')
      );
      
      // Bay beaches need 1 rookie each
      minRequired += bayBeaches.length;
      // Regular beaches need minimum 2 each
      minRequired += regularBeaches.length * 2;
      
      return minRequired;
    };

    const minStaffingRequired = calculateMinimumStaffing();
    const hasExcessStaffing = availableEmployees.length > minStaffingRequired + 2; // +2 buffer for roaming

    // Step 1: Handle special beaches first
    const bayBeaches = beaches.filter(b => b.name.toLowerCase().includes('bay'));
    const roamingBeach = beaches.find(b => b.name.toLowerCase().includes('roam'));

    // Step 2: Assign Captains to roaming immediately, defer LTs to help beaches reach minimum staffing
    if (roamingBeach) {
      const captains = unassigned.filter(emp => emp.rank === 'Captain');
      
      console.log(`🌊 Found roaming beach: ${roamingBeach.name}`);
      console.log(`👮 Step 2: Assigning Captains to roaming immediately, deferring LTs for beach minimum staffing`);
      console.log(`👑 Available captains: ${captains.length}`);
      
      // Assign ALL captains to roaming immediately (up to capacity)
      let roamingCapacity;
      if (roamingBeach.max_people !== null && roamingBeach.max_people !== undefined) {
        roamingCapacity = roamingBeach.max_people;
      } else {
        roamingCapacity = 7; // Fallback for backward compatibility
      }
      for (let i = 0; i < captains.length && newAssignments[roamingBeach.id].length < roamingCapacity; i++) {
        const captain = captains[i];
        newAssignments[roamingBeach.id].push(captain);
        const empIndex = unassigned.findIndex(e => e.id === captain.id);
        if (empIndex !== -1) {
          unassigned.splice(empIndex, 1);
        }
      }
      
      console.log(`🎯 Assigned ${newAssignments[roamingBeach.id].length} captains to roaming immediately:`, 
        newAssignments[roamingBeach.id].map(emp => `${emp.first_name} ${emp.last_name} (${emp.rank})`));
    }
    
    // Store roaming beach for later LT assignment
    const storedRoamingBeach = roamingBeach;

    // Step 3: Handle bay - only rookies, one per bay, prioritize rookies with fewest bay assignments
    const rookies = unassigned.filter(isRookie);
    
    // Sort rookies by bay assignment count (fewest first), then by seniority as tiebreaker
    const rookiesSortedByBayCount = [...rookies].sort((a, b) => {
      const aBayCount = rookieBayCount.get(a.id) || 0;
      const bBayCount = rookieBayCount.get(b.id) || 0;
      
      // Primary sort: fewest bay assignments first
      if (aBayCount !== bBayCount) {
        return aBayCount - bBayCount;
      }
      
      // Tiebreaker: least senior first (lower years of service gets priority for bay duty)
      // Apply Riggs family bonus for consistency with main sorting
      const aExperience = a.years_of_service + (a.last_name?.toLowerCase() === 'riggs' ? 1 : 0);
      const bExperience = b.years_of_service + (b.last_name?.toLowerCase() === 'riggs' ? 1 : 0);
      
      return aExperience - bExperience;
    });
    
    console.log(`🏖️ Bay assignment: ${bayBeaches.length} bay beaches, ${rookies.length} total rookies`);
    console.log(`📋 Rookie priority order (by bay count, then seniority):`);
    rookiesSortedByBayCount.forEach((rookie, index) => {
      const bayCount = rookieBayCount.get(rookie.id) || 0;
      const experience = rookie.years_of_service + (rookie.last_name?.toLowerCase() === 'riggs' ? 1 : 0);
      console.log(`  ${index + 1}. ${rookie.first_name} ${rookie.last_name} - ${bayCount} bay assignments, ${experience} years experience`);
    });
    
    let rookieIndex = 0;
    bayBeaches.forEach(beach => {
      if (newAssignments[beach.id].length === 0) { // Only assign if beach is empty
        if (rookieIndex < rookiesSortedByBayCount.length) {
          const assignedRookie = rookiesSortedByBayCount[rookieIndex];
          const bayCount = rookieBayCount.get(assignedRookie.id) || 0;
          console.log(`✅ Assigning rookie ${assignedRookie.first_name} ${assignedRookie.last_name} to bay beach ${beach.name} (${bayCount} previous bay assignments)`);
          newAssignments[beach.id].push(assignedRookie);
          const empIndex = unassigned.findIndex(e => e.id === assignedRookie.id);
          unassigned.splice(empIndex, 1);
          rookieIndex++;
        }
      }
    });

    // Step 3.5: Special Riggs family handling - if Riggs assigned to Bay PM, swap with Bay AM
    const bayAM = bayBeaches.find(b => b.name.toLowerCase().includes('am'));
    const bayPM = bayBeaches.find(b => b.name.toLowerCase().includes('pm'));
    
    if (bayAM && bayPM) {
      const bayAMAssignment = newAssignments[bayAM.id] || [];
      const bayPMAssignment = newAssignments[bayPM.id] || [];
      
      // Check if someone with last name "Riggs" is assigned to Bay PM
      const riggsInBayPM = bayPMAssignment.find(emp => 
        emp.last_name && emp.last_name.toLowerCase() === 'riggs'
      );
      
      if (riggsInBayPM && bayAMAssignment.length > 0) {
        const bayAMPerson = bayAMAssignment[0];
        
        console.log(`🔄 Special handling: ${riggsInBayPM.first_name} ${riggsInBayPM.last_name} assigned to Bay PM, swapping with ${bayAMPerson.first_name} ${bayAMPerson.last_name} from Bay AM`);
        
        // Swap the assignments
        newAssignments[bayAM.id] = [riggsInBayPM];
        newAssignments[bayPM.id] = [bayAMPerson];
        
        console.log(`✅ Swapped: ${riggsInBayPM.first_name} ${riggsInBayPM.last_name} now on Bay AM, ${bayAMPerson.first_name} ${bayAMPerson.last_name} now on Bay PM`);
      } else if (riggsInBayPM && bayAMAssignment.length === 0) {
        console.log(`⚠️ Special handling: ${riggsInBayPM.first_name} ${riggsInBayPM.last_name} assigned to Bay PM, but Bay AM is empty - no swap possible`);
      }
    }

    // Step 4: No special handling for 25th street (removed - now treated as normal beach)

    // Step 5: Assign to preferred beaches (with capacity limits to ensure fair distribution)
    // Process most senior employees first for preferred beach assignments
    console.log(`👑 Step 5: Assigning employees to preferred beaches (most senior first)`);
    
    const employeesToRemove = []; // Track employees to remove from unassigned
    
    for (let i = 0; i < unassigned.length; i++) {
      const employee = unassigned[i];
      if (!employee.beach) continue;
      
      const preferredBeach = beaches.find(b => 
        b.name.toLowerCase() === employee.beach.toLowerCase()
      );
      
      if (preferredBeach) {
        // Check capacity and balance - don't let preferred beaches get too many while others are empty
        const currentAssignment = newAssignments[preferredBeach.id];
        const currentCount = currentAssignment.length;
        
        // Get counts for all regular beaches (excluding bay and roaming)
        const regularBeachesForBalance = beaches.filter(b => 
          !b.name.toLowerCase().includes('bay') && 
          !b.name.toLowerCase().includes('roam')
        );
        const regularBeachCounts = regularBeachesForBalance.map(beach => ({
          beach,
          count: newAssignments[beach.id].length
        })).sort((a, b) => a.count - b.count);
        
        const minBeachCount = regularBeachCounts[0]?.count || 0;
        const maxBeachCount = Math.max(...regularBeachCounts.map(b => b.count));
        
        // Only allow assignment to preferred beach if:
        // 1. It won't create too much imbalance (no more than 1 person difference)
        // 2. We're still in the "fill to 2" phase, OR all beaches have at least 2
        const wouldCreateImbalance = (currentCount + 1) > (minBeachCount + 1);
        const allBeachesHaveTwo = minBeachCount >= 2;
        const isInFillToTwoPhase = !allBeachesHaveTwo;
        
        let canAssignToPreferred = false;
        
        if (isInFillToTwoPhase) {
          // During "fill to 2" phase, only assign if beach has less than 2 people
          canAssignToPreferred = currentCount < 2;
        } else {
          // After all beaches have 2, can assign third person if it doesn't create imbalance
          const maxCapacity = preferredBeach.max_people !== null && preferredBeach.max_people !== undefined 
            ? preferredBeach.max_people 
            : 3; // Fallback for backward compatibility
          canAssignToPreferred = currentCount < maxCapacity && !wouldCreateImbalance;
        }
        
        if (canAssignToPreferred) {
          // Check if this employee can be placed on their preferred beach
          if (isRookie(employee)) {
            if (canPlaceRookieOnBeach(employee, preferredBeach, currentAssignment)) {
              const maxCapacity = preferredBeach.max_people !== null && preferredBeach.max_people !== undefined 
              ? preferredBeach.max_people 
              : 3; // Fallback for backward compatibility
            console.log(`✅ Placing senior rookie ${employee.first_name} ${employee.last_name} (${employee.years_of_service} years) on preferred beach ${preferredBeach.name} (${currentCount + 1}/${maxCapacity})`);
              newAssignments[preferredBeach.id].push(employee);
              employeesToRemove.push(i);
            } else {
              console.log(`⚠️ Cannot place rookie ${employee.first_name} ${employee.last_name} on preferred beach ${preferredBeach.name} - rookie placement rules violated`);
            }
          } else {
            // Non-rookies can always be placed (prioritize them)
            const maxCapacity = preferredBeach.max_people !== null && preferredBeach.max_people !== undefined 
              ? preferredBeach.max_people 
              : 3; // Fallback for backward compatibility
            console.log(`✅ Placing senior ${employee.rank} ${employee.first_name} ${employee.last_name} (${employee.years_of_service} years) on preferred beach ${preferredBeach.name} (${currentCount + 1}/${maxCapacity})`);
            newAssignments[preferredBeach.id].push(employee);
            employeesToRemove.push(i);
          }
        } else {
          console.log(`⚖️ Skipping preferred beach ${preferredBeach.name} for ${employee.first_name} ${employee.last_name} (${employee.years_of_service} years) - ensuring fair distribution (current: ${currentCount}, min: ${minBeachCount})`);
        }
      }
    }
    
    // Remove assigned employees from unassigned list (in reverse order to maintain indices)
    employeesToRemove.reverse().forEach(index => {
      unassigned.splice(index, 1);
    });
    
    console.log(`👑 Preferred beach assignments complete. ${employeesToRemove.length} employees placed on preferred beaches.`);

    // Step 6: Enforce "2 people per beach before 3 people" rule with balanced distribution
    const regularBeaches = beaches.filter(b => 
      !b.name.toLowerCase().includes('bay') && 
      !b.name.toLowerCase().includes('roam')
    );

    console.log(`⚖️ Step 6: Ensuring ALL beaches get 2 people before ANY gets 3 people`);
    
    // Phase 6A: Fill all beaches to exactly 2 people (balanced round-robin)
    let attempts = 0;
    const maxAttempts = unassigned.length + 10; // Prevent infinite loops
    
    // Calculate if we have enough Lifeguards to staff beaches without using officers
    const totalLifeguards = unassigned.filter(emp => emp.rank === 'Lifeguard').length;
    const beachesNeedingStaffing = regularBeaches.filter(beach => newAssignments[beach.id].length < 2).length;
    const shouldUseOfficersForBeaches = totalLifeguards < beachesNeedingStaffing;
    
    console.log(`📊 Staffing calculation: ${totalLifeguards} Lifeguards available, ${beachesNeedingStaffing} beaches need staffing`);
    console.log(`${shouldUseOfficersForBeaches ? '⚠️ Not enough Lifeguards - will use officers for beaches' : '✅ Sufficient Lifeguards - officers go to roaming'}`);
    
    while (unassigned.length > 0 && attempts < maxAttempts) {
      attempts++;
      let assignmentMade = false;
      
      // Get beaches with less than 2 people, sorted by current count (fill emptiest first)
      const beachesNeedingPeople = regularBeaches
        .map(beach => ({
          beach,
          count: newAssignments[beach.id].length,
          needsPeople: newAssignments[beach.id].length < 2
        }))
        .filter(b => b.needsPeople)
        .sort((a, b) => a.count - b.count);
      
      if (beachesNeedingPeople.length === 0) {
        console.log(`✅ All regular beaches now have at least 2 people`);
        break; // All beaches have at least 2 people
      }
      
      // Try to assign to the beach with the fewest people
      for (const beachInfo of beachesNeedingPeople) {
        const { beach, count } = beachInfo;
        const currentAssignment = newAssignments[beach.id];
        
        // If beach is empty, ONLY use Lifeguards for first guard - save officers for roaming
        if (count === 0) {
          const lifeguard = unassigned.find(emp => emp.rank === 'Lifeguard');
          
          if (lifeguard) {
            console.log(`🏖️ Placing first guard (Lifeguard): ${lifeguard.first_name} ${lifeguard.last_name} on ${beach.name}`);
            newAssignments[beach.id].push(lifeguard);
            const empIndex = unassigned.findIndex(e => e.id === lifeguard.id);
            unassigned.splice(empIndex, 1);
            assignmentMade = true;
            break;
          }
          // If no Lifeguards available, skip this beach for now - officers should go to roaming first
        } else if (count === 1) {
          // Beach has 1 person, add second person
          // Use officers ONLY if we don't have enough Lifeguards to cover all beaches
          let nextEmployee;
          
          if (shouldUseOfficersForBeaches) {
            // Not enough Lifeguards - use officers to help reach minimum staffing
            nextEmployee = unassigned.find(emp => emp.rank === 'Lieutenant') ||
                          unassigned.find(emp => emp.rank === 'Lifeguard') ||
                          unassigned.find(emp => isRookie(emp)) ||
                          unassigned.find(emp => emp.rank === 'Sergeant') ||
                          unassigned.find(emp => emp.rank === 'Captain');
          } else {
            // Enough Lifeguards available - save officers for roaming
            nextEmployee = unassigned.find(emp => emp.rank === 'Lifeguard') ||
                          unassigned.find(emp => isRookie(emp));
          }

          if (nextEmployee) {
            // Validate rookie placement
            if (isRookie(nextEmployee)) {
              if (canPlaceRookieOnBeach(nextEmployee, beach, currentAssignment)) {
                console.log(`✅ Adding second guard (rookie): ${nextEmployee.first_name} ${nextEmployee.last_name} to ${beach.name}`);
                newAssignments[beach.id].push(nextEmployee);
                const empIndex = unassigned.findIndex(e => e.id === nextEmployee.id);
                unassigned.splice(empIndex, 1);
                assignmentMade = true;
                break;
              } else {
                // Try to find a non-rookie instead
                const alternativeEmployee = unassigned.find(emp => !isRookie(emp));
                if (alternativeEmployee) {
                  console.log(`✅ Adding second guard (non-rookie): ${alternativeEmployee.rank} ${alternativeEmployee.first_name} ${alternativeEmployee.last_name} to ${beach.name}`);
                  newAssignments[beach.id].push(alternativeEmployee);
                  const empIndex = unassigned.findIndex(e => e.id === alternativeEmployee.id);
                  unassigned.splice(empIndex, 1);
                  assignmentMade = true;
                  break;
                }
              }
            } else {
              // Non-rookie can always be placed
              console.log(`✅ Adding second guard: ${nextEmployee.rank} ${nextEmployee.first_name} ${nextEmployee.last_name} to ${beach.name}`);
              newAssignments[beach.id].push(nextEmployee);
              const empIndex = unassigned.findIndex(e => e.id === nextEmployee.id);
              unassigned.splice(empIndex, 1);
              assignmentMade = true;
              break;
            }
          }
        }
      }
      
      if (!assignmentMade) {
        console.warn(`⚠️ Could not make any assignments in round ${attempts} - breaking to prevent infinite loop`);
        break;
      }
    }
    
    // Log status after Phase 6A
    const beachCounts = regularBeaches.map(beach => `${beach.name}: ${newAssignments[beach.id].length}`);
    console.log(`📊 After Phase 6A (2-people minimum): ${beachCounts.join(', ')}`);
    console.log(`👥 Remaining unassigned: ${unassigned.length} employees`);

    // Step 6B: Now assign remaining LTs to roaming (after beaches have minimum staffing)
    // Note: Captains were already assigned to roaming in Step 2
    if (storedRoamingBeach) {
      const remainingLieutenants = unassigned.filter(emp => emp.rank === 'Lieutenant');
      
      console.log(`🌊 Step 6B: Now assigning LTs to roaming (beaches have minimum staffing)`);
      console.log(`👮 Available LTs for roaming: ${remainingLieutenants.length} (Captains already assigned in Step 2)`);
      
      // Assign remaining LTs to roaming (this is the default behavior the user wants)
      let roamingCapacity;
      if (storedRoamingBeach.max_people !== null && storedRoamingBeach.max_people !== undefined) {
        roamingCapacity = storedRoamingBeach.max_people;
      } else {
        roamingCapacity = 7; // Fallback for backward compatibility
      }
      const currentRoamingCount = newAssignments[storedRoamingBeach.id].length;
      
      for (let i = 0; i < remainingLieutenants.length && newAssignments[storedRoamingBeach.id].length < roamingCapacity; i++) {
        const lt = remainingLieutenants[i];
        newAssignments[storedRoamingBeach.id].push(lt);
        const empIndex = unassigned.findIndex(e => e.id === lt.id);
        if (empIndex !== -1) {
          unassigned.splice(empIndex, 1);
        }
      }
      
      console.log(`🎯 Total roaming assignments: ${newAssignments[storedRoamingBeach.id].length} officers (${currentRoamingCount} captains + ${newAssignments[storedRoamingBeach.id].length - currentRoamingCount} LTs):`, 
        newAssignments[storedRoamingBeach.id].map(emp => `${emp.first_name} ${emp.last_name} (${emp.rank})`));
    }

    // Step 6C: Assign remaining Sergeants to roaming if capacity allows (beaches have minimum staffing)
    if (storedRoamingBeach) {
      const remainingSergeants = unassigned.filter(emp => emp.rank === 'Sergeant');
      
      if (remainingSergeants.length > 0) {
        console.log(`👮 Step 6C: Assigning remaining Sergeants to roaming if capacity allows`);
        console.log(`👮 Available Sergeants for roaming: ${remainingSergeants.length}`);
        
        let roamingCapacity;
        if (storedRoamingBeach.max_people !== null && storedRoamingBeach.max_people !== undefined) {
          roamingCapacity = storedRoamingBeach.max_people;
        } else {
          roamingCapacity = 7; // Fallback for backward compatibility
        }
        for (let i = 0; i < remainingSergeants.length && newAssignments[storedRoamingBeach.id].length < roamingCapacity; i++) {
          const sergeant = remainingSergeants[i];
          newAssignments[storedRoamingBeach.id].push(sergeant);
          const empIndex = unassigned.findIndex(e => e.id === sergeant.id);
          if (empIndex !== -1) {
            unassigned.splice(empIndex, 1);
          }
          console.log(`✅ Assigned Sergeant ${sergeant.first_name} ${sergeant.last_name} to roaming`);
                 }
       }
     }

    // Step 6D: Handle any beaches that still need first guards (only if no Lifeguards were available earlier)
    const emptyBeaches = regularBeaches.filter(beach => newAssignments[beach.id].length === 0);
    if (emptyBeaches.length > 0) {
      console.log(`🏖️ Step 6D: Filling empty beaches with officers as last resort`);
      emptyBeaches.forEach(beach => {
        const officer = unassigned.find(emp => ['Lieutenant', 'Sergeant', 'Captain'].includes(emp.rank));
        if (officer) {
          console.log(`⚠️ Using officer as first guard (last resort): ${officer.rank} ${officer.first_name} ${officer.last_name} on ${beach.name}`);
          newAssignments[beach.id].push(officer);
          const empIndex = unassigned.findIndex(e => e.id === officer.id);
          unassigned.splice(empIndex, 1);
        }
      });
    }

    // Step 7: Fill remaining spots (max 3 per beach) with balanced distribution
    console.log(`⚖️ Step 7: Adding third person to beaches (preferred beaches first, then balanced)`);
    
    // Phase 7: Add third person to beaches in round-robin fashion (balanced)
    let round = 0;
    const maxRounds = Math.ceil(unassigned.length / regularBeaches.length) + 2;
    
    while (unassigned.length > 0 && round < maxRounds) {
      round++;
      let assignmentMade = false;
      
      // Get beaches that can accept a third person, sorted by current count (fill least staffed first)
      const beachesForThirdPerson = regularBeaches
        .map(beach => ({
          beach,
          count: newAssignments[beach.id].length,
          maxCapacity: beach.max_people !== null && beach.max_people !== undefined 
            ? beach.max_people 
            : 3 // Fallback for backward compatibility
        }))
        .filter(b => b.count < b.maxCapacity)
        .sort((a, b) => a.count - b.count);
      
      if (beachesForThirdPerson.length === 0) {
        console.log(`✅ All beaches are at maximum capacity`);
        break;
      }
      
      // Try to add one person to each beach that needs it, starting with least staffed
      for (const beachInfo of beachesForThirdPerson) {
        if (unassigned.length === 0) break;
        
        const { beach, count } = beachInfo;
        const currentAssignment = newAssignments[beach.id];
        
        // First, check if any unassigned employees have this beach as their preferred beach
        const preferredEmployee = unassigned.find(emp => 
          emp.beach && emp.beach.toLowerCase() === beach.name.toLowerCase()
        );
        
        let nextEmployee;
        
        if (preferredEmployee) {
          // Found someone who prefers this beach - prioritize them
          nextEmployee = preferredEmployee;
          console.log(`👑 Found preferred employee for ${beach.name}: ${nextEmployee.first_name} ${nextEmployee.last_name} (${nextEmployee.rank})`);
        } else {
          // No preferred employee found, use regular priority order
          nextEmployee = unassigned.find(emp => emp.rank === 'Lifeguard') ||
                        unassigned.find(emp => isRookie(emp)) ||
                        unassigned.find(emp => emp.rank === 'Sergeant') ||
                        unassigned.find(emp => emp.rank === 'Lieutenant') ||
                        unassigned.find(emp => emp.rank === 'Captain');
        }
        
        if (nextEmployee) {
          if (isRookie(nextEmployee)) {
            // Validate rookie placement thoroughly
            if (canPlaceRookieOnBeach(nextEmployee, beach, currentAssignment)) {
              const preferredNote = preferredEmployee ? ' (preferred beach)' : '';
              const maxCapacity = beach.max_people !== null && beach.max_people !== undefined 
                ? beach.max_people 
                : 3; // Fallback for backward compatibility
              console.log(`✅ Adding third guard (rookie): ${nextEmployee.first_name} ${nextEmployee.last_name} to ${beach.name} (${count + 1}/${maxCapacity})${preferredNote}`);
              newAssignments[beach.id].push(nextEmployee);
              const empIndex = unassigned.findIndex(e => e.id === nextEmployee.id);
              unassigned.splice(empIndex, 1);
              assignmentMade = true;
            } else {
              // Try to find a non-rookie instead
              const alternativeEmployee = unassigned.find(emp => !isRookie(emp));
              if (alternativeEmployee) {
                const maxCapacity = beach.max_people !== null && beach.max_people !== undefined 
                  ? beach.max_people 
                  : 3; // Fallback for backward compatibility
                console.log(`✅ Adding third guard (non-rookie alternative): ${alternativeEmployee.rank} ${alternativeEmployee.first_name} ${alternativeEmployee.last_name} to ${beach.name} (${count + 1}/${maxCapacity})`);
                newAssignments[beach.id].push(alternativeEmployee);
                const altIndex = unassigned.findIndex(e => e.id === alternativeEmployee.id);
                unassigned.splice(altIndex, 1);
                assignmentMade = true;
              }
            }
          } else {
            // Non-rookie can always be placed
            const preferredNote = preferredEmployee ? ' (preferred beach)' : '';
            const maxCapacity = beach.max_people !== null && beach.max_people !== undefined 
              ? beach.max_people 
              : 3; // Fallback for backward compatibility
            console.log(`✅ Adding third guard: ${nextEmployee.rank} ${nextEmployee.first_name} ${nextEmployee.last_name} to ${beach.name} (${count + 1}/${maxCapacity})${preferredNote}`);
            newAssignments[beach.id].push(nextEmployee);
            const empIndex = unassigned.findIndex(e => e.id === nextEmployee.id);
            unassigned.splice(empIndex, 1);
            assignmentMade = true;
          }
        }
      }
      
      if (!assignmentMade) {
        console.warn(`⚠️ Could not make any assignments in round ${round} of Step 7 - breaking`);
        break;
      }
    }
    
    // Final distribution status
    const finalBeachCounts = regularBeaches.map(beach => `${beach.name}: ${newAssignments[beach.id].length}`);
    console.log(`📊 Final distribution: ${finalBeachCounts.join(', ')}`);
    console.log(`👥 Final unassigned: ${unassigned.length} employees`);

    // Final validation: Check for over-assignment and balance issues
    console.log('🔍 Performing final validation for assignment balance and rookie placement rules...');
    let violationsFound = false;
    
    // Check for beaches with too many people and redistribute
    Object.entries(newAssignments).forEach(([beachId, employees]) => {
      const beach = beaches.find(b => b.id.toString() === beachId);
      if (!beach) return;
      
      const maxCapacity = beach.max_people !== null && beach.max_people !== undefined 
        ? beach.max_people 
        : (beach.name.toLowerCase().includes('bay') ? 1 : 
           beach.name.toLowerCase().includes('roam') ? 7 : 3); // Fallback for backward compatibility
      
      if (employees.length > maxCapacity) {
        violationsFound = true;
        console.error(`❌ OVER-ASSIGNMENT: Beach ${beach.name} has ${employees.length} people (max should be ${maxCapacity})!`);
        
        // Move excess employees back to unassigned (prioritize rookies first, then by seniority)
        const sortedEmployees = [...employees].sort((a, b) => {
          // Rookies first (to move them), then by years of service (least senior first)
          if (isRookie(a) && !isRookie(b)) return -1;
          if (!isRookie(a) && isRookie(b)) return 1;
          return a.years_of_service - b.years_of_service;
        });
        
        const keepEmployees = sortedEmployees.slice(0, 3);
        const moveToUnassigned = sortedEmployees.slice(3);
        
        if (moveToUnassigned.length > 0) {
          console.log(`🔧 Moving ${moveToUnassigned.length} excess employees from ${beach.name} back to unassigned:`, 
            moveToUnassigned.map(emp => `${emp.first_name} ${emp.last_name} (${emp.rank})`));
          
          newAssignments[beachId] = keepEmployees;
          moveToUnassigned.forEach(emp => unassigned.push(emp));
        }
      }
    });
    
    Object.entries(newAssignments).forEach(([beachId, employees]) => {
      const beach = beaches.find(b => b.id.toString() === beachId);
      if (!beach) return;
      
      if (!validateBeachAssignment(beach, employees)) {
        violationsFound = true;
        console.error(`❌ VIOLATION: Beach ${beach.name} has rookies sitting alone!`);
        console.log('Employees on this beach:', employees.map(emp => `${emp.first_name} ${emp.last_name} (${emp.rank})`));
        
        // Try to fix by moving rookies back to unassigned
        const rookiesOnBeach = employees.filter(isRookie);
        const nonRookiesOnBeach = employees.filter(emp => !isRookie(emp));
        
        if (rookiesOnBeach.length > 0 && nonRookiesOnBeach.length === 0) {
          console.log(`🔧 Moving ${rookiesOnBeach.length} rookies back to unassigned from ${beach.name}`);
          rookiesOnBeach.forEach(rookie => {
            unassigned.push(rookie);
          });
          newAssignments[beachId] = nonRookiesOnBeach;
        }
      }
    });
    
    if (violationsFound) {
      console.warn('⚠️ Some rookie placement violations were found and corrected');
    } else {
      console.log('✅ All rookie placement rules validated successfully');
    }

    setAssignments(newAssignments);
    
    // Log completion summary
    const totalAssigned = Object.values(newAssignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0);
    console.log(`🎯 Auto sort completed: ${totalAssigned} employees assigned, ${unassigned.length} remaining unassigned`);
    
    if (unassigned.length > 0) {
      console.log('Remaining unassigned employees:', unassigned.map(emp => `${emp.first_name} ${emp.last_name} (${emp.rank})`));
    }
    
    // Log final beach assignments
    console.log('📋 Final beach assignments:');
    Object.entries(newAssignments).forEach(([beachId, employees]) => {
      const beach = beaches.find(b => b.id.toString() === beachId);
      if (beach && employees.length > 0) {
        console.log(`  ${beach.name}: ${employees.map(emp => `${emp.first_name} ${emp.last_name} (${emp.rank})`).join(', ')}`);
      }
    });
  }, [availableEmployees, beaches]);

  // Save schedule assignments to database
  const saveSchedule = useCallback(async () => {
    try {
      setSaving(true);
      setSaveStatus('Saving schedule...');

      const totalAssignments = Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0);
      
      if (totalAssignments === 0) {
        setSaveStatus('No assignments to save');
        return;
      }

      console.log(`💾 Saving ${totalAssignments} assignments for ${selectedDate}`);

      const response = await apiClient.post('/api/schedule-assignments', {
        date: selectedDate,
        assignments: assignments,
        vehicleAssignments: vehicleAssignments
      });

      console.log('✅ Schedule saved successfully:', response.data);
      setSaveStatus(`Schedule saved successfully (${response.data.totalAssignments} assignments, ${response.data.totalVehicleAssignments || 0} vehicles)`);
      
      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);

    } catch (error) {
      console.error('Failed to save schedule:', error);
      setSaveStatus(`Error saving schedule: ${error.response?.data?.error || error.message}`);
      
      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setSaving(false);
    }
  }, [selectedDate, assignments, vehicleAssignments]);

  // Notify scheduled people
  const notifyScheduledPeople = useCallback(async () => {
    try {
      setNotifying(true);
      setSaveStatus('Sending notifications...');

      const totalAssignments = Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0);
      
      if (totalAssignments === 0) {
        setSaveStatus('No scheduled employees to notify');
        return;
      }

      // Get all assigned employees with their beach assignments
      const employeeNotifications = [];
      Object.entries(assignments).forEach(([beachId, beachEmployees]) => {
        const beach = beaches.find(b => b.id === parseInt(beachId));
        if (beach && beachEmployees.length > 0) {
          beachEmployees.forEach(emp => {
            employeeNotifications.push({
              userId: emp.id,
              beachName: beach.name
            });
          });
        }
      });

      if (employeeNotifications.length === 0) {
        setSaveStatus('No employees assigned to notify');
        return;
      }

      // Format the date for the notification using system time
      const today = new Date();
      const todayYear = today.getFullYear();
      const todayMonth = today.getMonth();
      const todayDay = today.getDate();
      const tomorrow = new Date(todayYear, todayMonth, todayDay + 1);
      
      // Create schedule date using local time to avoid timezone issues
      const [year, month, day] = selectedDate.split('-').map(Number);
      const scheduleDate = new Date(year, month - 1, day); // month is 0-indexed
      
      // Format dates for comparison using local system time
      const todayDateString = today.toDateString();
      const tomorrowDateString = tomorrow.toDateString();
      const scheduleDateString = scheduleDate.toDateString();

      let dayDescription;
      if (scheduleDateString === todayDateString) {
        dayDescription = 'today';
      } else if (scheduleDateString === tomorrowDateString) {
        dayDescription = 'tomorrow';
      } else {
        // Use system time formatting for consistent date display
        dayDescription = scheduleDate.toLocaleDateString('en-US', { 
          weekday: 'long',
          month: 'numeric', 
          day: 'numeric',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
      }

      // Send individual notifications with beach-specific information
      const notificationPromises = employeeNotifications.map(async ({ userId, beachName }) => {
        const notificationData = {
          userIds: [userId],
          title: 'Scheduled to Work',
          body: `You are scheduled to work ${dayDescription} on ${beachName}`
        };

        try {
          const response = await apiClient.post('/api/push/send-to-users', notificationData);
          return { success: true, userId, beachName, response: response.data };
        } catch (error) {
          console.error(`Failed to notify user ${userId} for ${beachName}:`, error);
          return { success: false, userId, beachName, error: error.message };
        }
      });

      console.log(`📱 Sending notifications to ${employeeNotifications.length} scheduled employees for ${dayDescription}`);

      const results = await Promise.all(notificationPromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      console.log('✅ Notifications completed:', { successCount, failureCount });
      
      if (failureCount === 0) {
        setSaveStatus(`Notifications sent to ${successCount} scheduled employees`);
      } else {
        setSaveStatus(`Notifications sent to ${successCount}/${employeeNotifications.length} employees (${failureCount} failed)`);
      }
      
      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);

    } catch (error) {
      console.error('Failed to send notifications:', error);
      setSaveStatus(`Error sending notifications: ${error.response?.data?.error || error.message}`);
      
      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setNotifying(false);
    }
  }, [selectedDate, assignments]);

  // Load data on mount and date change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBeaches(), fetchVehicles(), fetchEmployeesAndAvailability(), fetchUserPermissions()]);
      await loadExistingAssignments();
      setLoading(false);
    };
    loadData();
  }, [fetchBeaches, fetchVehicles, fetchEmployeesAndAvailability, fetchUserPermissions, loadExistingAssignments]);

  // Get unassigned employees
  const getUnassignedEmployees = () => {
    const assignedIds = new Set();
    Object.values(assignments).forEach(beachEmployees => {
      beachEmployees.forEach(emp => assignedIds.add(emp.id));
    });
    return availableEmployees.filter(emp => !assignedIds.has(emp.id));
  };

  // Get non-available employees (all employees except available ones)
  const getNonAvailableEmployees = () => {
    const availableIds = new Set(availableEmployees.map(emp => emp.id));
    const assignedIds = new Set(Object.values(assignments).flatMap(beachEmployees => beachEmployees.map(emp => emp.id)));
    return employees.filter(emp => !availableIds.has(emp.id) && !assignedIds.has(emp.id));
  };

  // Handle adding non-available employee to available pool
  const handleAddNonAvailableEmployee = (employee) => {
    setAvailableEmployees(prev => [...prev, employee]);
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Handle unassigning employees back to the pool
    if (activeId.toString().startsWith('assigned-') && overId === 'unassigned-pool') {
      const activeEmployeeId = parseInt(activeId.toString().replace('assigned-', ''));
      
      // Find the employee in current assignments and remove them
      const newAssignments = { ...assignments };
      let removedEmployee = null;
      
      for (const [beachId, employees] of Object.entries(newAssignments)) {
        const employeeIndex = employees.findIndex(emp => emp.id === activeEmployeeId);
        if (employeeIndex !== -1) {
          removedEmployee = employees[employeeIndex];
          newAssignments[beachId] = employees.filter(emp => emp.id !== activeEmployeeId);
          break;
        }
      }
      
      if (removedEmployee) {
        setAssignments(newAssignments);
        console.log(`Unassigned ${removedEmployee.first_name} ${removedEmployee.last_name} back to available pool`);
      }
      return;
    }

    // Handle reordering within beach zones
    if (activeId.toString().startsWith('assigned-') && overId.toString().startsWith('assigned-')) {
      const activeEmployeeId = parseInt(activeId.toString().replace('assigned-', ''));
      const overEmployeeId = parseInt(overId.toString().replace('assigned-', ''));
      
      // Find which beach contains these employees
      const beachId = Object.keys(assignments).find(bId => 
        assignments[bId].some(emp => emp.id === activeEmployeeId || emp.id === overEmployeeId)
      );
      
      if (beachId) {
        const beachEmployees = assignments[beachId];
        const oldIndex = beachEmployees.findIndex(emp => emp.id === activeEmployeeId);
        const newIndex = beachEmployees.findIndex(emp => emp.id === overEmployeeId);
        
        if (oldIndex !== newIndex) {
          const newEmployees = arrayMove(beachEmployees, oldIndex, newIndex);
          setAssignments({
            ...assignments,
            [beachId]: newEmployees
          });
        }
      }
      return;
    }

    // Handle moving assigned employee to different beach
    if (activeId.toString().startsWith('assigned-') && overId.toString().startsWith('beach-')) {
      const activeEmployeeId = parseInt(activeId.toString().replace('assigned-', ''));
      const targetBeachId = parseInt(overId.toString().replace('beach-', ''));
      
      // Find the employee in current assignments
      let draggedEmployee = null;
      let sourceBeachId = null;
      
      for (const [beachId, employees] of Object.entries(assignments)) {
        const found = employees.find(emp => emp.id === activeEmployeeId);
        if (found) {
          draggedEmployee = found;
          sourceBeachId = beachId;
          break;
        }
      }
      
      if (!draggedEmployee || !sourceBeachId) return;
      
      const targetBeach = beaches.find(b => b.id === targetBeachId);
      if (!targetBeach) return;

      const newAssignments = { ...assignments };
      
      // Check capacity and business rules
      if (!newAssignments[targetBeachId]) newAssignments[targetBeachId] = [];
      
      // Use the max_people field from the database if available
      let capacity;
      if (targetBeach.max_people !== null && targetBeach.max_people !== undefined) {
        capacity = targetBeach.max_people;
      } else {
        // Fallback to hardcoded values for backward compatibility
        capacity = targetBeach.name.toLowerCase().includes('bay') ? 1 : 
                   targetBeach.name.toLowerCase().includes('roam') ? 7 : 3;
      }

      if (newAssignments[targetBeachId].length < capacity) {
        // Check business rules before adding
        const canAssign = validateAssignment(draggedEmployee, targetBeach, newAssignments[targetBeachId]);
        if (canAssign) {
          // Remove from source beach
          newAssignments[sourceBeachId] = newAssignments[sourceBeachId].filter(emp => emp.id !== activeEmployeeId);
          // Add to target beach
          newAssignments[targetBeachId].push(draggedEmployee);
          setAssignments(newAssignments);
        }
      }
      return;
    }

    // Handle dropping unassigned employee on beach
    const draggedEmployee = availableEmployees.find(emp => emp.id === activeId);
    if (!draggedEmployee) return;

    // Handle dropping on beach
    if (overId.toString().startsWith('beach-')) {
      const beachId = parseInt(overId.toString().replace('beach-', ''));
      const beach = beaches.find(b => b.id === beachId);
      
      if (!beach) return;

      const newAssignments = { ...assignments };
      
      // Remove from current assignment
      Object.keys(newAssignments).forEach(bId => {
        newAssignments[bId] = newAssignments[bId].filter(emp => emp.id !== draggedEmployee.id);
      });

      // Add to new beach if capacity allows
      if (!newAssignments[beachId]) newAssignments[beachId] = [];
      
      // Use the max_people field from the database if available
      let capacity;
      if (beach.max_people !== null && beach.max_people !== undefined) {
        capacity = beach.max_people;
      } else {
        // Fallback to hardcoded values for backward compatibility
        capacity = beach.name.toLowerCase().includes('bay') ? 1 : 
                   beach.name.toLowerCase().includes('roam') ? 7 : 3;
      }

      if (newAssignments[beachId].length < capacity) {
        // Check business rules before adding
        const canAssign = validateAssignment(draggedEmployee, beach, newAssignments[beachId]);
        if (canAssign) {
          newAssignments[beachId].push(draggedEmployee);
          setAssignments(newAssignments);
        }
      }
    }
  };

  // Validate assignment based on business rules
  const validateAssignment = (employee, beach, currentEmployees) => {
    // All validation rules removed - allowing any employee to be placed on any beach
    // Complete drag and drop freedom
    return true;
  };

  // Vehicle assignment functions
  const generateSuggestedVehicleAssignments = useCallback(() => {
    // Get all assigned employees (includes both available and manually added employees)
    const assignedEmployees = Object.values(assignments).flatMap(beachEmployees => beachEmployees);
    
    if (!assignedEmployees.length || !vehicles.length) return {};

    const suggestedAssignments = {};
    const usedEmployeeIds = new Set();

    // Helper to rank by seniority (years of service only)
    const sortBySeniorityDesc = (a, b) => (b.years_of_service || 0) - (a.years_of_service || 0);

    // For each vehicle, suggest exactly one person: highest seniority eligible candidate
    vehicles.forEach(vehicle => {
      const isATV = vehicle.name?.toLowerCase().startsWith('atv');

      let candidates;
      if (isATV) {
        // ATVs: EMTs without a specific assigned vehicle in profile
        candidates = assignedEmployees.filter(emp => emp.emt_training && !emp.assigned_vehicle);
      } else {
        // Non-ATV named vehicles: only those who have this vehicle assigned in their profile
        candidates = assignedEmployees.filter(emp => emp.assigned_vehicle && emp.assigned_vehicle === vehicle.name);
      }

      // Ensure we only pick employees not already suggested and sort by seniority
      const ranked = candidates
        .filter(emp => !usedEmployeeIds.has(emp.id))
        .sort(sortBySeniorityDesc);

      const chosen = ranked[0];
      if (chosen) {
        suggestedAssignments[chosen.id] = {
          employee: chosen,
          vehicle,
          reason: isATV
            ? 'ATV: highest seniority EMT'
            : 'Assigned vehicle: highest seniority for this vehicle'
        };
        usedEmployeeIds.add(chosen.id);
      }
    });

    return suggestedAssignments;
  }, [vehicles, assignments]);

  const handleVehicleAssignment = (employeeId, vehicleId) => {
    const newAssignments = { ...vehicleAssignments };
    
    if (!vehicleId) {
      // Remove assignment
      delete newAssignments[employeeId];
    } else {
      // Add/update assignment
      // Look for employee in assigned employees (from all beach assignments)
      const allAssignedEmployees = Object.values(assignments).flatMap(beachEmployees => beachEmployees);
      const employee = allAssignedEmployees.find(emp => emp.id === employeeId);
      const vehicle = vehicles.find(v => v.id === vehicleId);
      
      if (employee && vehicle) {
        newAssignments[employeeId] = {
          employee,
          vehicle,
          reason: 'Manual assignment'
        };
      }
    }
    
    setVehicleAssignments(newAssignments);
  };

  const applyVehicleSuggestions = () => {
    const suggestions = generateSuggestedVehicleAssignments();
    setVehicleAssignments(suggestions);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading schedule generation...</p>
      </div>
    );
  }

  const activeEmployee = availableEmployees.find(emp => emp.id === activeId) ||
                        availableEmployees.find(emp => emp.id === parseInt(activeId?.toString().replace('assigned-', '') || '0')) ||
                        (() => {
                          // Find assigned employee being dragged
                          if (activeId && activeId.toString().startsWith('assigned-')) {
                            const employeeId = parseInt(activeId.toString().replace('assigned-', ''));
                            for (const beachEmployees of Object.values(assignments)) {
                              const found = beachEmployees.find(emp => emp.id === employeeId);
                              if (found) return found;
                            }
                          }
                          return null;
                        })();

  return (
    <div className="schedule-generation">
      <div className="schedule-header">
        <h1>Schedule Generation</h1>
        <div className="schedule-controls">
          <div className="date-selector">
            <label htmlFor="schedule-date">Select Date:</label>
            <input
              id="schedule-date"
              type="date"
              value={selectedDate}
              onChange={(e) => {
                console.log('📅 Date changed - will load existing assignments for new date');
                setSaveStatus('');
                setSelectedDate(e.target.value);
              }}
            />
          </div>
          <button className="btn btn-primary" onClick={autoSort}>
            Auto Sort
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowVehicleAssignment(true)}
            disabled={Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0) === 0}
          >
            Vehicle Assignment
          </button>
          <button 
            className="btn btn-success" 
            onClick={saveSchedule}
            disabled={saving || Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0) === 0}
          >
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
          {userPermissions.includes('send_notifications') && (
            <button 
              className="btn btn-warning" 
              onClick={notifyScheduledPeople}
              disabled={notifying || Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0) === 0}
              title="Send push notifications to all scheduled employees"
            >
              {notifying ? 'Notifying...' : '🔔 Notify Scheduled People'}
            </button>
          )}
        </div>
      </div>

      <div className="schedule-stats">
        <div className="stat">
          <span className="stat-number">{availableEmployees.length}</span>
          <span className="stat-label">Available</span>
        </div>
        <div className="stat">
          <span className="stat-number">{getUnassignedEmployees().length}</span>
          <span className="stat-label">Unassigned</span>
        </div>
        <div className="stat">
          <span className="stat-number">{Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0)}</span>
          <span className="stat-label">Assigned</span>
        </div>
        <div className="stat">
          <span className="stat-number">{beaches.length}</span>
          <span className="stat-label">Active Beaches</span>
        </div>
      </div>

      {saveStatus && (
        <div className={`save-status ${saveStatus.includes('Error') || saveStatus.includes('Error') ? 'error' : 'success'}`}>
          {saveStatus}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="schedule-layout">
          {/* Left Column - Unassigned Employees */}
          <div className="employees-column">
            <UnassignedSection 
              employees={getUnassignedEmployees()}
              activeId={activeId}
            />

            {/* Non-Available Employees */}
            <NonAvailableSection 
              employees={getNonAvailableEmployees()}
              onAddToSchedule={handleAddNonAvailableEmployee}
            />
          </div>

          {/* Beach Assignments */}
          <div className="beaches-section">
            <h2>Beach Assignments</h2>
            <div className="beaches-grid">
              {beaches.map(beach => (
                <BeachDropZone
                  key={beach.id}
                  beach={beach}
                  employees={assignments[beach.id] || []}
                  onDrop={(employee) => {}}
                  onReorder={(employees) => setAssignments({
                    ...assignments,
                    [beach.id]: employees
                  })}
                  beaches={beaches}
                  assignments={assignments}
                  vehicleAssignments={vehicleAssignments}
                />
              ))}
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeEmployee ? (
            <DraggableEmployee employee={activeEmployee} isActive={true} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Vehicle Assignment Overlay */}
      {showVehicleAssignment && (
        <div className="overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>Vehicle Assignment</h2>
              <button 
                className="close-btn"
                onClick={() => setShowVehicleAssignment(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {/* Status Banner */}
              <div className="status-banner suggested">
                <strong>Suggested Assignment</strong> - One person per vehicle (highest seniority). You can manually assign multiple people to a vehicle.
              </div>
              
              <div className="vehicle-assignment-controls">
                <button 
                  className="btn btn-primary"
                  onClick={applyVehicleSuggestions}
                >
                  Apply Suggestions
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setVehicleAssignments({})}
                >
                  Clear All
                </button>
              </div>

              <div className="vehicle-assignment-grid">
                <div className="assigned-employees-section">
                  <h3>Assigned Employees</h3>
                  <div className="employees-list">
                    {Object.values(assignments).flatMap(beachEmployees => beachEmployees).map(employee => {
                      const currentAssignment = vehicleAssignments[employee.id];
                      const availableVehicles = vehicles;
                      
                      return (
                        <div key={employee.id} className="employee-vehicle-row">
                          <div className="employee-info">
                            <div className="employee-name">
                              {employee.first_name && employee.last_name 
                                ? `${employee.first_name} ${employee.last_name}` 
                                : employee.username}, {employee.rank}
                            </div>
                            <div className="employee-details">
                              {employee.years_of_service > 0 && `${employee.years_of_service} years`}
                              {employee.emt_training && (
                                <span className="badge badge-success">EMT</span>
                              )}
                              {employee.advanced_training && (
                                <span className="badge badge-info">Advanced</span>
                              )}
                              {employee.assigned_vehicle && (
                                <span className="preferred-vehicle">
                                  Prefers: {employee.assigned_vehicle}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="vehicle-selector">
                            <select
                              value={currentAssignment?.vehicle.id || ''}
                              onChange={(e) => handleVehicleAssignment(employee.id, e.target.value ? parseInt(e.target.value) : null)}
                              className="form-control"
                            >
                              <option value="">No Vehicle</option>
                              {availableVehicles.map(vehicle => (
                                <option key={vehicle.id} value={vehicle.id}>
                                  {vehicle.name} {vehicle.description && `- ${vehicle.description}`}
                                </option>
                              ))}
                              {currentAssignment && (
                                <option value={currentAssignment.vehicle.id}>
                                  {currentAssignment.vehicle.name} (Currently Assigned)
                                </option>
                              )}
                            </select>
                            {currentAssignment && (
                              <div className="assignment-reason">
                                {currentAssignment.reason}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="vehicles-summary">
                  <h3>Vehicle Status</h3>
                  <div className="vehicles-list">
                    {vehicles.map(vehicle => {
                      const assignments = Object.values(vehicleAssignments).filter(
                        assignment => assignment.vehicle.id === vehicle.id
                      );
                      
                      return (
                        <div key={vehicle.id} className={`vehicle-status ${assignments.length > 0 ? 'assigned' : 'available'}`}>
                          <div className="vehicle-name">{vehicle.name}</div>
                          {vehicle.description && (
                            <div className="vehicle-description">{vehicle.description}</div>
                          )}
                          {assignments.length > 0 ? (
                            <div className="assigned-to">
                              Assigned to ({assignments.length}):
                              {assignments.map((assignment, index) => (
                                <div key={assignment.employee.id} className="assignment-entry">
                                  {assignment.employee.first_name && assignment.employee.last_name 
                                    ? `${assignment.employee.first_name} ${assignment.employee.last_name}` 
                                    : assignment.employee.username}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="available">Available</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={() => setShowVehicleAssignment(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleGeneration; 